import { Injectable } from "@angular/core";
import { Unsubscribe } from "@firebase/auth";
import { UserService } from "./user.service";
import { DiaryService } from "./diary.service";
import { lastValueFrom, Observable, take } from "rxjs";
import { PeriodEvent } from "../models/event";
import * as moment from "moment";

type LogCategory = 'symptoms' | 'moods' | 'medication' | 'diet' | 'supplements' | 'flow' | 'discharge' | 'sex' | 'contraception';

@Injectable({ providedIn: 'root' })
export class EventService {
    private unsubscribe: Unsubscribe | null = null;
    private readonly multi = new Set<LogCategory>(['symptoms', 'moods', 'medication', 'diet', 'supplements', 'flow', 'discharge', 'sex', 'contraception']);

    constructor(
        private diaryService: DiaryService,
    ) { }

    private _getTodaysEvent(): Observable<PeriodEvent> {
        return this.diaryService.getUserSymptoms(moment().format('YYYY-MM-DD')).pipe(take(1));
    }

    saveUserWeight(date: Date, weight: number): Promise<void> {
        let dateString = moment(date).format('YYYY-MM-DD');
        return this.diaryService.saveUserWeight(dateString, weight);
    }

    saveUserTemperature(date: Date, temperature: number): Promise<void> {
        let dateString = moment(date).format('YYYY-MM-DD');
        return this.diaryService.saveUserTemperature(dateString, temperature);
    }

    async logSymptom(key: string): Promise<void> { await this._logCategory('symptoms', key); }
    async logMood(key: string): Promise<void> { await this._logCategory('moods', key); }
    async logSupplements(key: string): Promise<void> { await this._logCategory('supplements', key); }
    async logDiet(key: string): Promise<void> { await this._logCategory('diet', key); }
    async logMedication(key: string): Promise<void> { await this._logCategory('medication', key); }
    async logSex(key: string): Promise<void> { await this._logCategory('sex', key); }
    async logContraception(key: string): Promise<void> { await this._logCategory('contraception', key); }
    async logFlow(key: string): Promise<void> { await this._logCategory('flow', key); }
    async logDischarge(key: string): Promise<void> { await this._logCategory('discharge', key); }

    private _getOptions(objectTitle: string): any[] {
        return this.diaryService.getSymptoms().find(s => s.objectTitle === objectTitle)?.items ?? [];
    }
    
    public getOptions(objectTitle: string): { id: number, label: string, value: string, icon: string }[] {
        return this.diaryService.getSymptoms().find(s => s.objectTitle === objectTitle)?.items ?? [];
    }

    public getHabits(healthy: boolean): { id: number, label: string, value: string, icon: string, healthy: boolean }[] {
        let habits = this.diaryService.getSymptoms().find(s => s.objectTitle === 'diet')?.items ?? [];
        return habits.filter((h) => h.healthy == healthy);
    }

    private _mergeIds(current: number[] | undefined, add: number[]): number[] {
        return Array.from(new Set([...(current ?? []), ...add]));
    }

    private async _logCategory(category: LogCategory, key: string): Promise<void> {
        const keys = key.split(',').map(k => k.trim()).filter(Boolean);
        if (!keys.length) return;

        const items = this._getOptions(category);
        const matches = items.filter((i: any) => keys.includes(i.value));
        if (!matches.length) return;

        const today = moment().format('YYYY-MM-DD');

        try {
            let event = await lastValueFrom(this._getTodaysEvent()).catch(() => undefined);
            if (!event) {
                event = new PeriodEvent();
                event.date = today;
            }

            if (this.multi.has(category)) {
                const ids = matches.map((i: any) => i.id);
                (event as any)[category] = this._mergeIds((event as any)[category], ids);
            } else {
                const id = matches[matches.length - 1].id;
                (event as any)[category] = id;
            }

            await this.diaryService.savePeriodEvent(today, event);
        } catch (err) {
            console.error(`Error logging ${category}:`, err);
        }
    }

    mapPeriodEventForFormatter(event: PeriodEvent): {
        date: Date;
        moods?: { title: string }[];
        symptoms?: { title: string }[];
        contraception?: { title: string }[];
        sex?: { title: string }[];
        flow?: { title: string } | null;
        discharge?: { title: string } | null;
    } {
        let date: Date;
        if (event?.date) {
            const [y, m, d] = event.date.split('-').map(Number);
            date = new Date((y as number) || 1970, ((m as number) ?? 1) - 1, (d as number) || 1, 0, 0, 0, 0);
        } else {
            date = new Date();
            date.setHours(0, 0, 0, 0);
        }

        const moods = this._mapIdsToTitles('moods', event.moods);
        const symptoms = this._mapIdsToTitles('symptoms', event.symptoms);
        const contraception = this._mapIdsToTitles('contraception', event.contraception);
        const sex = this._mapIdsToTitles('sex', event.sex);

        const flowArr = this._mapIdsToTitles('flow', event.flow != null ? [event.flow] : undefined);
        const dischargeArr = this._mapIdsToTitles('discharge', event.discharge != null ? [event.discharge] : undefined);

        return {
            date,
            moods,
            symptoms,
            contraception,
            sex,
            flow: flowArr?.[0] ?? null,
            discharge: dischargeArr?.[0] ?? null,
        };
    }

    private _mapIdsToTitles(objectTitle: string, ids?: number[] | null): { title: string }[] | undefined {
        if (!ids || ids.length === 0) return undefined;

        const options = this._getOptions(objectTitle); // uses your fn
        if (!options || options.length === 0) return undefined;

        const idToLabel = new Map<number, string>(options.map((o: any) => [o.id, o.label]));
        const out: { title: string }[] = [];
        const seen = new Set<string>();

        for (const id of ids) {
            const label = idToLabel.get(Number(id));
            if (label && !seen.has(label)) {
                out.push({ title: label });
                seen.add(label); // de-dupe, keep order
            }
        }
        return out.length ? out : undefined;
    }

    hasPdAssistantDataLogged(e: {
        symptoms?: number[]; sex?: number[]; contraception?: number[]; moods?: number[];
        discharge?: number | null; flow?: number | null;
    }): boolean {
        return !!(
            (e.symptoms && e.symptoms.length) ||
            (e.sex && e.sex.length) ||
            (e.contraception && e.contraception.length) ||
            (e.moods && e.moods.length) ||
            e.discharge != null ||
            e.flow != null
        );
    }
}
